import React from 'react';
import { StyleSheet, Text, View, Modal, Dimensions } from 'react-native';
import GenericButton from '../molecules/GenericButton';
import { useAuth } from 'react-oidc-context';
import { useThemeContext } from '../../contexts';
import { getResponsivePadding, getResponsiveFontSize } from '../../helpers/utils';

const { width } = Dimensions.get('window');

interface LogoutModal {
  modalVisible: boolean;
  setModalVisible: (modalVisible: boolean) => void;
}

/**
 * Logout modal component with responsive design
 *
 * @param {LogoutModal} props
 * @return {React.FC}
 */
const LogoutModal: React.FC<LogoutModal> = ({ modalVisible, setModalVisible }) => {
  const { theme } = useThemeContext();
  const authContext = useAuth();
  const confirmLogout = async () => {
    authContext.signoutRedirect();
    setModalVisible(false);
  };

  const styles = createStyles(theme);

  return (
    <Modal visible={modalVisible} transparent={true} onRequestClose={() => setModalVisible(false)}>
      <View style={styles.modalContainer}>
        <View style={styles.modalContent}>
          <Text style={styles.textStyle}>Are you sure you want to log out?</Text>
          <View style={styles.buttonContainer}>
            <GenericButton
              isPrimary
              title="YES"
              isDisabled={false}
              onPress={confirmLogout}
              buttonStyle={styles.primaryButton}
              textStyle={styles.buttonText}
            />
            <GenericButton
              isPrimary={false}
              title="NO"
              isDisabled={false}
              onPress={() => {
                setModalVisible(false);
              }}
              buttonStyle={styles.secondaryButton}
              textStyle={styles.secondaryButtonText}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default LogoutModal;

const createStyles = (theme: any) =>
  StyleSheet.create({
    modalContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      padding: getResponsivePadding(16, 20, 24)
    },
    modalContent: {
      backgroundColor: 'white',
      padding: getResponsivePadding(16, 20, 24),
      borderRadius: 10,
      width: '100%',
      maxWidth: Math.min(width * 0.9, 350),
      minWidth: Math.min(width * 0.8, 280),
      alignItems: 'center'
    },
    textStyle: {
      fontSize: getResponsiveFontSize(14, 15, 16),
      fontWeight: '600',
      textAlign: 'center',
      marginBottom: getResponsivePadding(16, 18, 20),
      color: '#1C274C'
    },
    buttonContainer: {
      flexDirection: width < 400 ? 'column' : 'row',
      justifyContent: 'space-between',
      width: '100%',
      gap: getResponsivePadding(8, 10, 12)
    },
    primaryButton: {
      backgroundColor: theme.primary,
      minWidth: width < 400 ? '100%' : 85,
      height: 40,
      borderRadius: 6,
      flex: width < 400 ? 0 : 1,
      marginRight: width < 400 ? 0 : 8
    },
    secondaryButton: {
      backgroundColor: 'white',
      minWidth: width < 400 ? '100%' : 85,
      height: 40,
      borderRadius: 6,
      borderColor: 'Black',
      borderWidth: 1,
      flex: width < 400 ? 0 : 1
    },
    buttonText: {
      fontSize: getResponsiveFontSize(14, 15, 18),
      fontWeight: '700',
      color: 'white'
    },
    secondaryButtonText: {
      fontSize: getResponsiveFontSize(14, 15, 18),
      fontWeight: '700',
      color: 'Black'
    }
  });
